// Pestaña «Gasto» de la Sala de Control (sprint 135 de Venuz; rediseño aprobado por Pau el 07-10-2026). Responde en
// este orden: 1) ¿servir Calypso nos sale rentable frente al objetivo? (titular: el % de nuestra parte que se come el
// gasto); 2) ¿por qué? (un gráfico y en qué se va); 3) a demanda, cuentas que pierden y el detalle técnico. Todo sale
// del libro diario del servidor, que se mantiene solo; aquí no se calcula nada que pueda pudrirse (ni tarifas ni avisos).
import { CalendarDays, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { t, type TKey } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { Aviso, PistaAyuda } from '../ui';
import { salaApi } from './api';
import { CacheGasto } from './CacheGasto';
import { DetalleTecnico, FilaPlegable, FOCO, PanelCuentas } from './DetalleGasto';
import { dineroExacto, edad, numero, SIN_VALOR } from './formato';
import { Exacto } from './Puntero';
import { AnilloObjetivo, ColumnasPorcentajeDia, HorasRegistradas } from './GraficosGasto';
import {
  agrupar,
  COMPARTIDA,
  cuadrePorProveedor,
  CUADRA,
  DIA,
  diasDe,
  dosCifras,
  estadoDe,
  fecha,
  GRUPOS,
  hora,
  horaMinuto,
  horaDeSello,
  INICIO_LIBRO,
  INICIO_REGISTRO,
  iso,
  mejorDia,
  nombreMes,
  nombreCuadre,
  OBJETIVO,
  pct,
  PERIODOS,
  puntosDif,
  queda,
  rangoDe,
  rangoEstimados,
  rangoFechas,
  sumarDias,
  TEXTO_ESTADO,
  usd,
  type Periodo,
} from './logicaGasto';
import type { AvisoGastoSala, GastoSala } from './tipos';
import { useTamano } from './useTamano';

export { agrupar, GRUPOS, OBJETIVO, rangoDe } from './logicaGasto';

/** Foto del gasto de unas fechas, refrescada cada minuto. Si falla, se queda la última y se reintenta sola. */
function useGasto(desde: string, hasta: string, activo = true) {
  const [foto, setFoto] = useState<GastoSala | null>(null);
  const [fallo, setFallo] = useState(false);
  const [vuelta, setVuelta] = useState(0);
  useEffect(() => {
    let vivo = true;
    setFoto(null);
    setFallo(false);
    if (!activo) return;
    const pedir = () =>
      salaApi
        .gasto(desde, hasta)
        .then((f) => {
          if (!vivo) return;
          setFoto(f);
          setFallo(false);
        })
        .catch(() => vivo && setFallo(true));
    void pedir();
    const id = setInterval(pedir, 60_000);
    return () => {
      vivo = false;
      clearInterval(id);
    };
  }, [desde, hasta, activo, vuelta]);
  return { foto, fallo, reintentar: () => setVuelta((v) => v + 1) };
}

/** Disposición según el ancho real de la pestaña (la ventana de la sala no ocupa toda la pantalla). */
type Modo = 'ancho' | 'medio' | 'estrecho';
const modoDe = (ancho: number | null): Modo => (ancho === null || ancho >= 990 ? 'ancho' : ancho >= 840 ? 'medio' : 'estrecho');

export function Gasto({ ahora = Date.now }: { ahora?: () => number }) {
  // Se abre en «Hoy»: el % es comparable aunque el día vaya a medias (gasto y ventas del mismo tramo de horas).
  const [periodo, setPeriodo] = useState<Periodo>('hoy');
  const [fechas, setFechas] = useState(() => ({ desde: iso(ahora() - 7 * DIA), hasta: iso(ahora() - DIA) }));
  const hoy = iso(ahora());
  // `hoy` en las dependencias: con la pestaña abierta al pasar el día UTC, «Hoy» pasa al día nuevo en la siguiente vuelta.
  const { desde, hasta } = useMemo(() => rangoDe(periodo, ahora(), fechas), [periodo, fechas, ahora, hoy]);
  const { foto, fallo, reintentar } = useGasto(desde, hasta);
  const mesRango = rangoDe('mes', ahora(), fechas);
  const esMes = mesRango.desde === desde && mesRango.hasta === hasta;
  const mes = useGasto(mesRango.desde, mesRango.hasta, !esMes);
  const [ref, tam] = useTamano<HTMLDivElement>();
  const modo = modoDe(tam?.ancho ?? null);

  const unDia = desde === hasta;
  const etiqueta = etiquetaPeriodo(periodo, desde, hasta, hoy, foto);
  return (
    <div ref={ref} className="flex min-h-full flex-col gap-4">
      <div className={cn('flex gap-4', modo === 'estrecho' ? 'flex-col items-start gap-2' : 'items-center justify-between')}>
        <SelectorPeriodo periodo={periodo} alElegir={setPeriodo} />
        <span className="text-o-sub text-oficina-texto2 cifras">{etiqueta}</span>
      </div>
      {periodo === 'fechas' ? <CamposFechas fechas={fechas} alCambiar={setFechas} ahora={ahora} /> : null}

      {!foto && fallo ? (
        <Aviso tono="error">
          {t('salaControl.gasto.error')}{' '}
          <button type="button" onClick={reintentar} className={cn('ml-1 min-h-0 min-w-0 font-semibold underline underline-offset-4', FOCO)}>
            {t('salaControl.gasto.reintentar')}
          </button>
        </Aviso>
      ) : !foto ? (
        <EsqueletoGasto modo={modo} unDia={unDia} />
      ) : foto.diasConDatos === 0 ? (
        <SinDatos esHoy={desde === hoy && hasta === hoy} alVerHoy={() => setPeriodo('hoy')} />
      ) : (
        <Contenido key={`${desde}${hasta}`} foto={foto} mes={esMes ? foto : mes.foto} hoy={hoy} modo={modo} />
      )}
      <CacheGasto desde={desde} hasta={hasta} />
      {foto ? <Pie foto={foto} fallo={fallo} ahora={ahora} /> : null}
    </div>
  );
}

function etiquetaPeriodo(periodo: Periodo, desde: string, hasta: string, hoy: string, foto: GastoSala | null): string {
  if (desde !== hasta) {
    const base = rangoFechas(desde, hasta);
    return foto && foto.diasConDatos < foto.dias ? `${base} · ${t('salaControl.gasto.diasConDatos', { n: foto.diasConDatos, m: foto.dias })}` : base;
  }
  if (desde === hoy) {
    const tr = foto?.tramoHoy;
    const base = t('salaControl.gasto.etiquetaHoy', { fecha: fecha(desde) });
    return tr ? `${base} · ${t('salaControl.gasto.tramo', { desde: hora(tr.desde), hasta: `${dosCifras(tr.hasta)}:59` })}` : base;
  }
  const base = periodo === 'ayer' ? t('salaControl.gasto.etiquetaAyer', { fecha: fecha(desde) }) : fecha(desde);
  if (!foto) return base;
  const d = foto.estimadoAntesDe ? 0 : (foto.desdeHoraDia ?? 0);
  return `${base} · ${d > 0 ? t('salaControl.gasto.diaDesde', { hora: hora(d) }) : t('salaControl.gasto.diaCompleto')}`;
}

function SelectorPeriodo({ periodo, alElegir }: { periodo: Periodo; alElegir: (p: Periodo) => void }) {
  const boton = (p: Periodo, sel: boolean) =>
    cn(
      'h-10 min-h-10 min-w-10 whitespace-nowrap rounded-o-s text-o-texto font-medium transition-colors',
      // 22-10 (revisión de la sala): el elegido como en el selector del Pulso (gris y en negrita); el amarillo es de
      // «actuar» (botones, permisos), no de «estás viendo esto».
      sel ? 'bg-oficina-hover font-semibold text-oficina-texto' : 'text-oficina-texto2 hover:bg-oficina-hover hover:text-oficina-texto',
      FOCO,
      p === 'fechas' ? 'grid w-10 place-items-center' : 'px-4',
    );
  return (
    <div role="group" aria-label={t('salaControl.gasto.periodo')} className="flex max-w-full gap-0.5 overflow-x-auto rounded-o-m bg-oficina-elevada p-1">
      {PERIODOS.map((p) => (
        <button key={p} type="button" aria-pressed={p === periodo} onClick={() => alElegir(p)} className={boton(p, p === periodo)}>
          {t(`salaControl.gasto.periodos.${p}` as TKey)}
        </button>
      ))}
      <button type="button" aria-pressed={periodo === 'fechas'} aria-label={t('salaControl.gasto.periodos.fechas')} onClick={() => alElegir('fechas')} className={boton('fechas', periodo === 'fechas')}>
        <CalendarDays className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

function CamposFechas({ fechas, alCambiar, ahora }: { fechas: { desde: string; hasta: string }; alCambiar: (f: { desde: string; hasta: string }) => void; ahora: () => number }) {
  const campo = cn('h-10 rounded-o-s border border-oficina-linea bg-oficina-elevada px-3 text-oficina-texto [color-scheme:dark]', FOCO);
  const min = iso(ahora() - 399 * DIA);
  const max = iso(ahora());
  return (
    <div className="flex flex-wrap items-center gap-3 text-o-texto text-oficina-texto2">
      <label className="flex items-center gap-2">
        {t('salaControl.gasto.desde')}
        <input type="date" value={fechas.desde} min={min} max={max} onChange={(e) => e.target.value && alCambiar({ ...fechas, desde: e.target.value })} className={campo} />
      </label>
      <label className="flex items-center gap-2">
        {t('salaControl.gasto.hasta')}
        <input type="date" value={fechas.hasta} min={min} max={max} onChange={(e) => e.target.value && alCambiar({ ...fechas, hasta: e.target.value })} className={campo} />
      </label>
    </div>
  );
}

const TARJETA = 'rounded-o-m bg-oficina-elevada';

function Contenido({ foto, mes, hoy, modo }: { foto: GastoSala; mes: GastoSala | null; hoy: string; modo: Modo }) {
  const unDia = foto.desde === foto.hasta;
  const esHoy = unDia && foto.desde === hoy;
  const [cuentasAbierta, setCuentasAbierta] = useState(false);
  const [detalleAbierto, setDetalleAbierto] = useState(false);
  const [irAlDetalle, setIrAlDetalle] = useState(0);
  const detalleRef = useRef<HTMLElement>(null);
  const idCuentas = useId();
  const idDetalle = useId();
  useEffect(() => {
    if (irAlDetalle) detalleRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [irAlDetalle]);
  const verDetalle = () => {
    setDetalleAbierto(true);
    setIrAlDetalle((n) => n + 1);
  };

  // Un día estimado no tiene horas: se enseña frente a su semana (una petición más, solo en ese caso).
  const semana = useGasto(sumarDias(foto.desde, -6), foto.desde, unDia && !esHoy && !foto.porHora);
  const dias = useMemo(() => diasDe(foto), [foto]);
  const avisos = foto.avisos.filter((a) => a.nivel !== 'tecnico');
  const sinRegistroPorCuenta = foto.porDia.every((d) => d.origen === 'estimado');
  const perdedoras = foto.cuentasPerdedoras;

  const cuentas = esHoy ? (
    <FilaPlegable titulo={t('salaControl.gasto.cuentas')} texto={t('salaControl.gasto.cuentasHoy')} />
  ) : perdedoras.cuentas > 0 ? (
    <FilaPlegable
      titulo={t('salaControl.gasto.cuentas')}
      texto={t(perdedoras.cuentas === 1 ? 'salaControl.gasto.cuentasResumenUna' : 'salaControl.gasto.cuentasResumen', {
        n: numero(perdedoras.cuentas),
        usd: usd(Math.abs(perdedoras.total)),
      })}
      abierta={cuentasAbierta}
      alCambiar={() => setCuentasAbierta((v) => !v)}
      controla={idCuentas}
    />
  ) : (
    <FilaPlegable
      titulo={t('salaControl.gasto.cuentas')}
      texto={sinRegistroPorCuenta ? t('salaControl.gasto.cuentasSinRegistro', { fecha: fecha(INICIO_REGISTRO) }) : t('salaControl.gasto.cuentasNinguna')}
    />
  );
  const subDetalle = !unDia
    ? 'salaControl.gasto.detalleSubDias'
    : foto.porDia.some((d) => d.origen === 'registro')
      ? 'salaControl.gasto.detalleSubDia'
      : 'salaControl.gasto.detalleSubEstimado';

  return (
    <>
      <TitularGasto foto={foto} mes={mes} hoy={hoy} apilado={modo !== 'ancho'} estrecho={modo === 'estrecho'} alVerDetalle={verDetalle} />
      {avisos.length ? <Avisos avisos={avisos} /> : null}
      <div className={cn('grid gap-4', modo === 'ancho' && 'grid-cols-[minmax(0,1.64fr)_minmax(0,1fr)]')}>
        <BloqueGrafico foto={foto} semana={semana.foto} dias={dias} hoy={hoy} alto={modo === 'ancho' ? 248 : 184} />
        <ListaPartidas foto={foto} columnas={modo === 'ancho' ? 1 : modo === 'medio' ? 3 : 2} />
      </div>
      <div className={cn('grid gap-4', modo !== 'estrecho' && 'grid-cols-2')}>
        {cuentas}
        <FilaPlegable
          titulo={t('salaControl.gasto.detalle')}
          texto={t(subDetalle)}
          abierta={detalleAbierto}
          alCambiar={() => setDetalleAbierto((v) => !v)}
          controla={idDetalle}
        />
      </div>
      {cuentasAbierta && !esHoy && perdedoras.cuentas > 0 ? <PanelCuentas id={idCuentas} foto={foto} /> : null}
      {detalleAbierto ? <DetalleTecnico ref={detalleRef} id={idDetalle} foto={foto} unDia={unDia} dias={dias} hoy={hoy} /> : null}
    </>
  );
}

// ── Titular ──

function contexto(foto: GastoSala, hoy: string): ReactNode {
  const p = foto.porcentaje;
  if (p === null) return null;
  const puntos = Math.round(p * 100) - Math.round(OBJETIVO * 100);
  const n = Math.abs(puntos);
  const cifra = n === 1 ? t('salaControl.gasto.punto') : t('salaControl.gasto.puntos', { n: numero(n) });
  const frente =
    puntos === 0 ? (
      <b className="font-semibold text-oficina-texto">{t('salaControl.gasto.enObjetivo')}</b>
    ) : (
      <>
        <b className="font-semibold text-oficina-texto">{cifra}</b> {t(puntos > 0 ? 'salaControl.gasto.porEncima' : 'salaControl.gasto.porDebajo')}
      </>
    );
  let cola: string | null = null;
  if (foto.desde === foto.hasta) {
    const ant = foto.anterior;
    if (ant.diasConDatos > 0 && ant.porcentaje !== null) {
      const clave = foto.desde === hoy ? 'salaControl.gasto.ayerFue' : foto.desde === sumarDias(hoy, -1) ? 'salaControl.gasto.anteayerFue' : 'salaControl.gasto.diaAnteriorFue';
      const texto = t(clave, { pct: pct(ant.porcentaje) });
      cola = ant.hayEstimado ? t('salaControl.gasto.conEstimado', { texto }) : texto;
    }
  } else {
    const m = mejorDia(foto, hoy);
    if (m) cola = t('salaControl.gasto.mejorDia', { fecha: fecha(m.dia), pct: pct(m.p) });
  }
  return (
    <>
      {frente}
      {cola ? ` · ${cola}` : null}
    </>
  );
}
interface Nota {
  clave: string;
  icono: 'info' | 'ok';
  texto: string;
  detalle?: boolean;
}

/** Notas bajo el titular, solo cuando aplican: hoy a medias, compensaciones restadas y el cuadre con lo que nos cobraron. */
export function notasTitular(foto: GastoSala, esHoy: boolean): Nota[] {
  const out: Nota[] = [];
  const desdeH = foto.tramoHoy?.desde ?? foto.desdeHoraDia ?? 0;
  if (foto.estimadoAntesDe) out.push({ clave: 'estimado', icono: 'info', texto: t('salaControl.gasto.notaEstimadoAntes', { hora: horaMinuto(foto.estimadoAntesDe) }) });
  else if (esHoy && desdeH > 0) out.push({ clave: 'hoy', icono: 'info', texto: t('salaControl.gasto.notaHoyDesde', { hora: hora(desdeH) }) });
  const fm = foto.facturaManda;
  if (fm) {
    const clave = fm.facturado > fm.apuntado ? 'salaControl.gasto.notaFacturaMandaCortadas' : 'salaControl.gasto.notaFacturaManda';
    const dias = foto.desde === foto.hasta ? '' : t('salaControl.gasto.notaFacturaDias', { n: fm.dias });
    out.push({ clave: 'factura', icono: 'ok', texto: t(clave, { facturado: usd(fm.facturado), apuntado: usd(fm.apuntado), dias }) });
  }
  for (const c of foto.compensado ?? []) out.push({ clave: `compensado:${c.dia}:${c.proveedor}`, icono: 'ok', texto: t('salaControl.gasto.notaCompensado', { usd: usd(c.usd), motivo: c.motivo }) });
  // Con OpenRouter contado por su factura, su cuadre ya no es una nota: es la cifra.
  const cuadre = cuadrePorProveedor(foto.cuadre).filter((c) => c.diferencia !== null && !(fm && c.proveedor === 'openrouter'));
  // Primero lo que cuadra; después lo que se queda corto (con «Ver detalle»).
  for (const c of cuadre)
    if (Math.abs(c.diferencia!) <= CUADRA)
      out.push({ clave: c.clave, icono: 'ok', texto: t('salaControl.gasto.cuadreOk', { proveedor: nombreCuadre(c), n: puntosDif(c.diferencia!) }) });
  for (const c of cuadre)
    if (c.diferencia! < COMPARTIDA)
      out.push({ clave: c.clave, icono: 'info', texto: t('salaControl.gasto.cuadreMenor', { proveedor: nombreCuadre(c), n: puntosDif(c.diferencia!) }), detalle: true });
  return out;
}

function Cifra({ titulo, valor, sub, ayuda, exacto }: { titulo: string; valor: string; sub: string; ayuda?: string; exacto?: string }) {
  return (
    <div className="min-w-0">
      <div className="flex h-5 items-center gap-1 text-o-sub leading-5 text-oficina-texto2">
        {titulo}
        {ayuda ? (
          <PistaAyuda texto={ayuda}>
            <button type="button" aria-label={ayuda} className={cn('relative -m-0.5 inline-grid h-6 min-h-0 w-6 min-w-0 place-items-center rounded-md text-oficina-texto3 after:absolute after:-inset-2 hover:text-oficina-texto', FOCO)}>
              <Info className="h-3.5 w-3.5" aria-hidden />
            </button>
          </PistaAyuda>
        ) : null}
      </div>
      {exacto ? (
        <Exacto filas={[{ etiqueta: '', valor: exacto }]}>
          <p className="mt-1 text-o-titulo font-semibold leading-8 tracking-tight cifras">{valor}</p>
        </Exacto>
      ) : (
        <p className="mt-1 text-o-titulo font-semibold leading-8 tracking-tight cifras">{valor}</p>
      )}
      <p className="mt-1 max-w-[11rem] text-o-nota leading-4 text-oficina-texto3 cifras">{sub}</p>
    </div>
  );
}

function TitularGasto({
  foto,
  mes,
  hoy,
  apilado,
  estrecho,
  alVerDetalle,
}: {
  foto: GastoSala;
  mes: GastoSala | null;
  hoy: string;
  apilado: boolean;
  estrecho: boolean;
  alVerDetalle: () => void;
}) {
  const esHoy = foto.desde === foto.hasta && foto.desde === hoy;
  const p = foto.porcentaje;
  const tr = esHoy ? foto.tramoHoy : null;
  const ayerAEstaHora = tr && foto.porHoraAnterior ? foto.porHoraAnterior.slice(tr.desde, tr.hasta + 1).reduce((a, b) => a + b, 0) : null;
  const subGastado = esHoy
    ? tr
      ? [t('salaControl.gasto.gastadoTramo', { desde: hora(tr.desde), hasta: `${dosCifras(tr.hasta)}:59` }), ...(ayerAEstaHora !== null ? [t('salaControl.gasto.ayerAEstaHora', { usd: usd(ayerAEstaHora) })] : [])].join(' · ')
      : t('salaControl.gasto.sinApuntesHoy')
    : foto.porDia.every((d) => d.origen === 'estimado')
      ? t('salaControl.gasto.estimadoConConsumo')
      : foto.hayEstimado || foto.estimadoAntesDe
        ? t('salaControl.gasto.enParteEstimado')
        : t('salaControl.gasto.registrado');
  const notas = notasTitular(foto, esHoy);
  // «En octubre: …» da contexto a un periodo más corto; en «Este mes» repetiría el titular.
  const mesVisible = mes && mes !== foto && mes.diasConDatos > 0 && mes.porcentaje !== null;
  return (
    <section aria-label={t('salaControl.gasto.titularAria')} className={cn(TARJETA, 'px-8 py-6')}>
      <div className={cn('grid items-start', apilado ? 'grid-cols-1 gap-6' : 'grid-cols-[1fr_auto] gap-12')}>
        {/* 03-11 (rebrand Apple): el % dentro de un anillo de Actividad con la raya del objetivo, y al lado la frase. */}
        <div className="min-w-0">
        <p className="mb-3 text-o-cuerpo text-oficina-texto2">{t('salaControl.gasto.frase')}</p>
        <div className={cn('flex min-w-0 items-center', estrecho ? 'gap-4' : 'gap-7')}>
          <AnilloObjetivo p={p}>
            {p === null ? (
              <span className="text-o-titulo font-bold cifras">{pct(p)}</span>
            ) : (
              <Exacto filas={[{ etiqueta: '', valor: `${numero(p * 100, 2)} %` }]}>
                <span className={cn('text-o-titulo font-bold cifras', TEXTO_ESTADO[estadoDe(p)])}>{pct(p)}</span>
              </Exacto>
            )}
            <span className="text-o-nota text-oficina-texto2">{t('salaControl.gasto.deNuestraParte')}</span>
          </AnilloObjetivo>
          <div className="min-w-0">
            <p className="text-o-nota font-medium text-oficina-texto3">{t('salaControl.gasto.barraObjetivo')}</p>
            {p !== null ? <p className="mt-3 text-o-texto text-oficina-texto2 cifras">{contexto(foto, hoy)}</p> : null}
            {mesVisible ? (
              <p className="mt-2 text-o-nota leading-4 text-oficina-texto3 cifras">
                {t('salaControl.gasto.enMes', { mes: nombreMes(mes.desde), pct: pct(mes.porcentaje), usd: usd(mes.total) })}
              </p>
            ) : null}
          </div>
        </div>
        </div>
        <div
          className={cn(
            'grid',
            apilado ? cn('grid-cols-3 border-t border-oficina-linea pt-6', estrecho ? 'gap-4' : 'gap-8') : 'mt-1 grid-cols-[repeat(3,auto)] gap-8 border-l border-oficina-linea pl-8',
          )}
        >
          <Cifra titulo={t('salaControl.gasto.gastado')} valor={usd(foto.total)} exacto={dineroExacto(foto.total)} sub={subGastado} />
          <Cifra
            titulo={t('salaControl.gasto.nuestraParte')}
            ayuda={t('salaControl.gasto.nuestraParteAyuda')}
            valor={foto.parte === null ? SIN_VALOR : usd(foto.parte)}
            exacto={foto.parte === null ? undefined : dineroExacto(foto.parte)}
            sub={foto.ventasNeto === null ? '' : t('salaControl.gasto.deNetos', { usd: usd(foto.ventasNeto) })}
          />
          <Cifra
            titulo={t('salaControl.gasto.nosQueda')}
            valor={foto.parte === null ? SIN_VALOR : queda(foto.parte, foto.totalConVentas)}
            exacto={foto.parte === null ? undefined : dineroExacto(foto.parte - foto.totalConVentas)}
            sub={t('salaControl.gasto.nosQuedaDetalle')}
          />
        </div>
      </div>
      {notas.length ? (
        <ul className="mt-6 flex flex-col gap-2 border-t border-oficina-linea pt-4">
          {notas.map((n) => (
            <li key={n.clave} className="flex items-start gap-2 text-o-sub leading-5 text-oficina-texto2 cifras">
              {n.icono === 'ok' ? (
                <CircleCheck className="mt-[3px] h-3.5 w-3.5 flex-none text-oficina-trabajando" aria-hidden />
              ) : (
                <Info className="mt-[3px] h-3.5 w-3.5 flex-none text-oficina-texto3" aria-hidden />
              )}
              <span className="flex-1">
                {n.texto}
                {n.detalle ? (
                  <button
                    type="button"
                    onClick={alVerDetalle}
                    className={cn('relative ml-1 min-h-0 min-w-0 rounded-sm text-oficina-texto underline decoration-oficina-texto3 underline-offset-[3px] after:absolute after:-inset-x-1 after:-inset-y-2.5', FOCO)}
                  >
                    {t('salaControl.gasto.verDetalle')}
                  </button>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

// ── Avisos (de decisión y de negocio; los técnicos van al detalle) ──

function Avisos({ avisos }: { avisos: AvisoGastoSala[] }) {
  return (
    <div className="flex flex-col gap-2">
      {avisos.map((a) => {
        const decision = a.nivel === 'decision';
        return (
          <div
            key={a.clave}
            role="status"
            className={cn('flex items-start gap-3 rounded-o-m px-4 py-3', decision ? 'bg-oficina-acento/[0.08]' : 'bg-oficina-bloqueado/[0.08]')}
          >
            <TriangleAlert className={cn('mt-0.5 h-4 w-4 flex-none', decision ? 'text-oficina-acento' : 'text-oficina-bloqueado')} aria-hidden />
            <p className="text-o-texto leading-5">
              <b className={cn('mr-2 font-semibold', decision ? 'text-oficina-acento' : 'text-oficina-bloqueado')}>
                {t(decision ? 'salaControl.gasto.nivel.decision' : 'salaControl.gasto.nivel.aviso')}
              </b>
              {a.texto}
            </p>
          </div>
        );
      })}
    </div>
  );
}

// ── Gráfico y «En qué se va» ──

function Caja({ titulo, sub, children, nota }: { titulo: string; sub?: string; children: ReactNode; nota?: string | null }) {
  return (
    <section className={cn(TARJETA, 'min-w-0 p-6')}>
      <div className="mb-4 flex flex-wrap items-baseline gap-x-2">
        <h3 className="text-o-cuerpo font-semibold leading-6">{titulo}</h3>
        {sub ? <span className="text-o-sub text-oficina-texto3">{sub}</span> : null}
      </div>
      {children}
      {nota ? (
        <p className="mt-4 flex items-center gap-2 text-o-nota leading-4 text-oficina-texto3">
          <span className="rayado-estimado inline-block h-3 w-3 flex-none rounded-sm" aria-hidden />
          {nota}
        </p>
      ) : null}
    </section>
  );
}

function BloqueGrafico({
  foto,
  semana,
  dias,
  hoy,
  alto,
}: {
  foto: GastoSala;
  semana: GastoSala | null;
  dias: Array<{ dia: string; d: GastoSala['porDia'][number] | null }>;
  hoy: string;
  alto: number;
}) {
  const unDia = foto.desde === foto.hasta;
  const esHoy = unDia && foto.desde === hoy;
  if (unDia && foto.porHora) {
    const tr = esHoy ? foto.tramoHoy : null;
    // Horas estimadas: las anteriores al registro (día de la subida) o el día entero si es un día estimado.
    // La hora del corte (12:22) va con las apuntadas: la mayor parte de esa hora ya es registro.
    const estimadas = foto.estimadoAntesDe !== null ? Math.floor(foto.estimadoAntesDe) : foto.porDia[0]?.origen === 'estimado' ? 24 : 0;
    const primera = estimadas > 0 ? 0 : tr ? tr.desde : (foto.desdeHoraDia ?? 0);
    const ultima = esHoy ? (tr ? tr.hasta : -1) : 23;
    const nota =
      estimadas >= 24
        ? t('salaControl.gasto.notaDiaHorasEstimadas', { dia: fecha(foto.desde) })
        : estimadas > 0
          ? t('salaControl.gasto.notaHorasEstimadas')
          : null;
    return (
      <Caja titulo={t('salaControl.gasto.porHora')} sub={t('salaControl.gasto.porHoraSub')} nota={nota}>
        {ultima >= primera ? (
          <HorasRegistradas porHora={foto.porHora} primera={primera} ultima={ultima} enCurso={esHoy} estimadasAntesDe={estimadas} alto={alto} />
        ) : (
          <p className="py-6 text-o-texto text-oficina-texto3">{t('salaControl.gasto.sinApuntesGrafica')}</p>
        )}
      </Caja>
    );
  }
  if (unDia) {
    const titulo = t('salaControl.gasto.frenteSemana', { dia: foto.desde === sumarDias(hoy, -1) ? t('salaControl.gasto.ayerTitulo') : fecha(foto.desde) });
    const nota = foto.porDia[0]?.origen === 'estimado' ? t('salaControl.gasto.notaDiaEstimado', { dia: fecha(foto.desde), inicio: fecha(INICIO_REGISTRO) }) : null;
    return (
      <Caja titulo={semana ? titulo : t('salaControl.gasto.porDia')} sub={t('salaControl.gasto.porDiaSub')} nota={nota}>
        {semana ? (
          <ColumnasPorcentajeDia dias={diasDe(semana)} hoy={hoy} resaltar={foto.desde} />
        ) : (
          <ColumnasPorcentajeDia dias={dias} hoy={hoy} resaltar={foto.desde} />
        )}
      </Caja>
    );
  }
  const est = rangoEstimados(foto);
  return (
    <Caja titulo={t('salaControl.gasto.porDia')} sub={t('salaControl.gasto.porDiaSub')} nota={est ? t('salaControl.gasto.notaEstimados', { dias: est }) : null}>
      <ColumnasPorcentajeDia dias={dias} hoy={hoy} />
    </Caja>
  );
}

function ListaPartidas({ foto, columnas }: { foto: GastoSala; columnas: 1 | 2 | 3 }) {
  const g = agrupar(foto.partidas);
  return (
    <section className={cn(TARJETA, 'min-w-0 p-6')}>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
        <h3 className="text-o-cuerpo font-semibold leading-6">{t('salaControl.gasto.enQueSeVa')}</h3>
        {foto.hayEstimado ? <span className="text-o-sub text-oficina-texto3">{t('salaControl.gasto.repartoAproximado')}</span> : null}
      </div>
      <ul className={cn(columnas === 1 ? 'flex flex-col' : cn('grid gap-x-8 gap-y-2', columnas === 3 ? 'grid-cols-3' : 'grid-cols-2'))}>
        {GRUPOS.map((gr) => {
          const v = g[gr.clave];
          const cuota = foto.total > 0 ? v / foto.total : 0;
          return (
            <li key={gr.clave} className="min-h-12 py-1">
              <div className="flex items-center gap-2">
                <span className={cn('h-2 w-2 flex-none rounded-full', gr.clase)} aria-hidden />
                <span className="flex-1 text-o-texto">{t(`salaControl.gasto.grupos.${gr.clave}` as TKey)}</span>
              </div>
              <div className="ml-4 mt-1 flex h-5 items-center gap-3">
                <span className="h-1 flex-1 rounded-sm bg-oficina-hover">
                  <i className={cn('block h-1 rounded-sm', gr.clase)} style={{ width: `${Math.max(cuota * 100, 0.6)}%` }} />
                </span>
                <Exacto filas={[{ etiqueta: '', valor: dineroExacto(v) }, { etiqueta: t('salaControl.grafica.delGasto'), valor: `${numero(cuota * 100, 1)} %`, tenue: true }]}>
                  <span className="min-w-14 text-right text-o-texto font-semibold cifras">{usd(v)}</span>
                </Exacto>
                <span className="w-10 text-right text-o-sub text-oficina-texto3 cifras">{pct(cuota)}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ── Estados y pie ──

function Esq({ className, style }: { className: string; style?: CSSProperties }) {
  return <span style={style} className={cn('block animate-pulse rounded-md bg-oficina-hover motion-reduce:animate-none', className)} />;
}

function EsqueletoGasto({ modo, unDia }: { modo: Modo; unDia: boolean }) {
  return (
    <div aria-busy="true" aria-label={t('salaControl.gasto.cargandoAria')} className="flex flex-col gap-4">
      <section className={cn(TARJETA, 'px-8 py-6')}>
        <div className={cn('grid items-start', modo === 'ancho' ? 'grid-cols-[1fr_auto] gap-12' : 'grid-cols-1 gap-6')}>
          <div>
            <Esq className="h-4 w-48" />
            <Esq className="mt-3 h-12 w-64 rounded-o-s" />
            <Esq className="mt-5 h-2 w-full max-w-[440px] rounded" />
            <Esq className="mt-6 h-3.5 w-72" />
          </div>
          <div className={cn('grid grid-cols-3 gap-8', modo === 'ancho' && 'border-l border-oficina-linea pl-8')}>
            {[0, 1, 2].map((i) => (
              <div key={i}>
                <Esq className="h-3.5 w-16" />
                <Esq className="mt-2.5 h-7 w-24" />
                <Esq className="mt-2.5 h-3 w-28" />
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className={cn('grid gap-4', modo === 'ancho' && 'grid-cols-[minmax(0,1.64fr)_minmax(0,1fr)]')}>
        <section className={cn(TARJETA, 'p-6')}>
          <Esq className="h-[18px] w-48" />
          <div className="mt-12 flex h-36 items-end justify-around pl-11">
            {(unDia ? [45, 92, 86] : [120, 96, 88, 92, 108, 104, 112]).map((h, i) => (
              <Esq key={i} className="w-12 rounded" style={{ height: `${Math.min(h, 140)}px` }} />
            ))}
          </div>
        </section>
        <section className={cn(TARJETA, 'p-6')}>
          <Esq className="h-[18px] w-28" />
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="py-2">
              <Esq className="h-3.5 w-3/5" />
              <Esq className="mt-2.5 h-1 w-full rounded-sm" />
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

function SinDatos({ esHoy, alVerHoy }: { esHoy: boolean; alVerHoy: () => void }) {
  // Hoy, nada más empezar el día UTC (07:00 en Tailandia), el libro aún no lo ha calculado: no es «sin gasto».
  if (esHoy)
    return (
      <section className={cn(TARJETA, 'flex flex-col items-start gap-2 px-8 py-12')}>
        <p className="text-o-titulo3 font-semibold leading-6">{t('salaControl.gasto.calculandoHoyTitulo')}</p>
        <p className="text-o-texto text-oficina-texto2">{t('salaControl.gasto.calculandoHoySub')}</p>
      </section>
    );
  return (
    <section className={cn(TARJETA, 'flex flex-col items-start gap-2 px-8 py-12')}>
      <p className="text-o-titulo3 font-semibold leading-6">{t('salaControl.gasto.vacioTitulo')}</p>
      <p className="text-o-texto text-oficina-texto2">
        {t('salaControl.gasto.vacioSub', { inicio: fecha(INICIO_LIBRO), fin: fecha(sumarDias(INICIO_REGISTRO, -1)) })}
      </p>
      <button type="button" onClick={alVerHoy} className={cn('mt-4 h-10 min-h-10 rounded-o-s border border-oficina-linea bg-oficina-hover px-4 font-medium', FOCO)}>
        {t('salaControl.gasto.verHoy')}
      </button>
    </section>
  );
}

function Pie({ foto, fallo, ahora }: { foto: GastoSala; fallo: boolean; ahora: () => number }) {
  const hace = foto.libro_at ? edad(Math.max(0, Math.round((ahora() - Date.parse(foto.libro_at)) / 1000))) : null;
  const hasta = horaDeSello(foto.frescura.costes);
  const partes = [
    hace ? t(fallo ? 'salaControl.gasto.sinRefrescar' : 'salaControl.gasto.actualizado', { edad: hace }) : null,
    hasta ? t('salaControl.gasto.registradoHasta', { hora: hasta }) : null,
  ].filter(Boolean);
  if (!partes.length) return null;
  // Pie de la ventana (una sola línea de frescura): pegado abajo mientras se desplaza la pestaña.
  return (
    <p
      className={cn(
        'sticky -bottom-5 z-10 -mx-5 -mb-5 mt-auto flex min-h-10 items-center border-t border-oficina-linea bg-oficina-panel px-5 text-o-nota leading-4 cifras',
        fallo ? 'text-oficina-bloqueado' : 'text-oficina-texto3',
      )}
    >
      {partes.join(' · ')}
    </p>
  );
}
